import { Enums } from "@/helpers/enums";
import { Ajax, MethodType } from "@/helpers/web";
import { appData, C, J } from "@/helpers/global";
import { Constants } from "@/helpers/constants";

import { ILogger, LogLevel, HttpTransportType, HubConnectionBuilder, HubConnectionState } from "@microsoft/signalr";
import store from "@/store";
import router from '@/router';
import { StoreUser, StoreNotification } from "@/store/constants";
import { UserService } from "@/services/api/user";

const notificationListener = {
    connection: null,
    startPromise: null,
    stopPromise: null,
    retryTimer: null,
    enabled: false,
    syncing: false,
    receivedIds: new Set()
};

export const NotificationService = {
    List(callback, wait) {
        Ajax.Api(MethodType.Get, Enums.SERVICE.NOTIFICATION, null, wait).then((data) => {
            /*for (let i = 0; i < data.length; i++) {
                const r = data[i];
                r.sendDate = new Date(r.sendDate);
                //r.isReaded = C.Bool(r.isReaded);
                //r.userNotificationTypeName = J.FindKey(Constants.Notification.Type, r.userNotificationType);
            }*/
            callback(data);
        });
    },
    SaveIsReaded(j, callback) {
        Ajax.Api(MethodType.Put, Enums.SERVICE.NOTIFICATION + "/is-readed", j).then((data) => {
            callback(data);
        });
    },
    async RequestBrowserPermission() {
        if (!("Notification" in window)) {
            return "unsupported";
        }
        if (window.Notification.permission !== "default") {
            return window.Notification.permission;
        }
        return window.Notification.requestPermission();
    },
    async ListenServer() {
        const service = this;

        if (notificationListener.stopPromise) {
            await notificationListener.stopPromise;
        }

        if (notificationListener.startPromise) {
            return notificationListener.startPromise;
        }

        if (notificationListener.connection && notificationListener.connection.state !== HubConnectionState.Disconnected) {
            return notificationListener.connection.state !== HubConnectionState.Disconnecting;
        }

        notificationListener.enabled = true;
        clearTimeout(notificationListener.retryTimer);
        notificationListener.retryTimer = null;

        const retry = () => {
            if (!notificationListener.enabled || notificationListener.retryTimer !== null) {
                return;
            }

            notificationListener.retryTimer = setTimeout(() => {
                notificationListener.retryTimer = null;

                if (notificationListener.enabled && router.currentRoute.value.meta.theme === Enums.THEME.CLASSIC) {
                    service.ListenServer().catch((error) => console.warn("Notification retry error:", error));
                }
            }, 5000);
        };

        const syncUnread = async (connection) => {
            if (!notificationListener.enabled || connection !== notificationListener.connection || connection.state !== HubConnectionState.Connected) {
                return;
            }

            notificationListener.syncing = true;

            try {
                const endDate = store.getters[StoreNotification.getEndSendDateUnReaded];

                await connection.invoke("receiveNotificationGetUnReaded", endDate == null ? null : new Date(endDate));
            } catch (error) {
                console.warn("Unread notification sync error:", error);
            } finally {
                notificationListener.syncing = false;
            }
        };

        notificationListener.startPromise = Promise.resolve().then(async () => {
            const loggedIn = await UserService.GetTokenIsLogin();

            if (!notificationListener.enabled) {
                return false;
            }

            if (!loggedIn) {
                retry();
                return false;
            }

            if (!notificationListener.connection) {
                const connection = new HubConnectionBuilder()
                    .withUrl(appData.serverBase + "hubs/user/notification", {
                        accessTokenFactory: () => UserService.GetAccessRefreshToken(),
                        transport: HttpTransportType.WebSockets
                    })
                    .configureLogging(LogLevel.Critical)
                    .withAutomaticReconnect([0, 3000, 5000, 10000])
                    .build();

                notificationListener.connection = connection;

                connection.on("receiveError", (error) => console.warn("Received error:", error));

                connection.on("receiveNotificationAdd", (id, notificationType, subject, message, data, isReaded, sendDate) => {
                    if (!notificationListener.enabled || connection !== notificationListener.connection) {
                        return;
                    }

                    try {
                        const numericId = C.Int(id);
                        const exists = (store.state.notification?.list || []).some((item) => C.Int(item.id) === numericId);

                        if (numericId <= 0 || exists || notificationListener.receivedIds.has(numericId)) {
                            return;
                        }

                        notificationListener.receivedIds.add(numericId);

                        if (notificationListener.receivedIds.size > 2000) {
                            notificationListener.receivedIds.delete(notificationListener.receivedIds.values().next().value);
                        }

                        store.dispatch(StoreNotification.setList, [{
                            id,
                            notificationType,
                            subject,
                            message,
                            data,
                            isReaded,
                            sendDate
                        }]);

                        if (!notificationListener.syncing && !C.Bool(isReaded) && ("Notification" in window) && window.Notification.permission === "granted") {
                            new window.Notification(subject, { body: message });
                        }
                    } catch (error) {
                        console.warn("Error processing notification:", error);
                    }
                });

                connection.onreconnecting(() => {
                    notificationListener.syncing = true;
                });

                connection.onreconnected(() => syncUnread(connection));
                connection.onclose(retry);
            }

            notificationListener.syncing = true;
            await notificationListener.connection.start();

            if (!notificationListener.enabled) {
                return false;
            }

            await syncUnread(notificationListener.connection);

            return notificationListener.enabled && notificationListener.connection?.state === HubConnectionState.Connected;
        }).catch((error) => {
            notificationListener.syncing = false;
            console.error("Notification connection error:", error);
            retry();
            throw error;
        }).finally(() => {
            notificationListener.startPromise = null;
        });

        return notificationListener.startPromise;
    },
    StopListenServer() {
        if (notificationListener.stopPromise) {
            return notificationListener.stopPromise;
        }

        notificationListener.enabled = false;
        clearTimeout(notificationListener.retryTimer);
        notificationListener.retryTimer = null;

        notificationListener.stopPromise = Promise.resolve().then(async () => {
            try {
                if (notificationListener.startPromise) {
                    await notificationListener.startPromise.catch(() => {});
                }

                const connection = notificationListener.connection;
                notificationListener.connection = null;

                if (connection) {
                    await connection.stop();
                }
            } finally {
                notificationListener.syncing = false;
                notificationListener.receivedIds.clear();
            }
        }).finally(() => {
            notificationListener.stopPromise = null;
        });

        return notificationListener.stopPromise;
    }
};